// ** react imports
import React, { useState } from 'react';

// ** react hook form imports
import { Controller } from 'react-hook-form';

// ** components imports
import FileUploader from '../components/FileUploader';

// Text Input Component
const TextInput = ({
  data,
  control,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
}) => {
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);

  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <div className="relative">
        <input
          type="text"
          value={currentValue || ''}
          onChange={e => handleChange(e.target.value)}
          placeholder={
            data.placeholder || `Enter ${data.questionText.toLowerCase()}`
          }
          className={`${baseInputClasses} ${errorInputClasses}`}
          disabled={!isEditMode}
          required={data.isRequired}
        />
        {showCopiedFeedback && (
          <div
            className="absolute -top-6 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded z-10"
            role="alert"
          >
            Copied!
          </div>
        )}
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <div className="relative">
            <input
              type="text"
              value={value || currentValue || ''}
              onChange={e => {
                onChange(e.target.value);
                handleChange(e.target.value);
              }}
              placeholder={
                data.placeholder || `Enter ${data.questionText.toLowerCase()}`
              }
              className={`${baseInputClasses} ${errorInputClasses}`}
              disabled={!isEditMode}
              required={data.isRequired}
              {...rest}
            />
            {showCopiedFeedback && (
              <div
                className="absolute -top-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded z-10"
                role="alert"
              >
                Copied!
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

// Group Input Component (non-repeating grouped fields rendered horizontally)
const GroupInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatDateForInput,
}) => {
  const fields = Array.isArray(data?.fields) ? data.fields : [];
  const currentValue =
    data && typeof data.answer === 'object' && !Array.isArray(data.answer)
      ? data.answer
      : {};

  const renderField = (field, valueObj, setObj) => {
    const t = (field?.questionType || field?.inputType || 'text').toLowerCase();
    const name = field.questionId;
    const val = valueObj?.[name] ?? '';
    const commonProps = {
      disabled: !isEditMode,
      required: field.required,
      placeholder:
        field.placeholder ||
        `Enter ${field.questionText?.toLowerCase?.() || 'value'}`,
      className: `${baseInputClasses} ${errorInputClasses}`,
    };

    switch (t) {
      case 'currency':
      case 'number':
        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
              {t === 'currency' ? '$' : ''}
            </span>
            <input
              type="number"
              value={val || ''}
              onChange={e => setObj({ ...valueObj, [name]: e.target.value })}
              onWheel={e => e.target.blur()}
              min={field.validation?.min}
              max={field.validation?.max}
              step={t === 'currency' ? '0.01' : field.validation?.step}
              className={`${t === 'currency' ? 'pl-8 ' : ''}no-spinners ${baseInputClasses} ${errorInputClasses}`}
              disabled={!isEditMode}
              required={field.required}
              placeholder={commonProps.placeholder}
            />
          </div>
        );
      case 'date':
        return (
          <input
            type="date"
            value={(formatDateForInput && formatDateForInput(val)) || ''}
            onChange={e => setObj({ ...valueObj, [name]: e.target.value })}
            {...commonProps}
          />
        );
      default:
        return (
          <input
            type="text"
            value={val || ''}
            onChange={e => setObj({ ...valueObj, [name]: e.target.value })}
            {...commonProps}
          />
        );
    }
  };

  if (!control) {
    let valueObj = currentValue || {};
    const setLocal = next => {
      valueObj = next;
      if (isEditMode && handleInputChange) {
        handleInputChange(data.questionId, next);
      }
    };
    return (
      <div className="w-full">
        <div className="flex flex-col md:flex-row gap-3 md:gap-4 md:items-end">
          {fields.map(f => (
            <div key={f.questionId} className="flex-1 min-w-[140px]">
              {f.questionText && (
                <label className="block text-sm mb-1 text-gray-700 dark:text-gray-300">
                  {f.questionText}
                </label>
              )}
              {renderField(f, valueObj, setLocal)}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || {}}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        const valueObj = value && typeof value === 'object' ? value : {};
        const setObj = next => {
          onChange(next);
          if (isEditMode && handleInputChange) {
            handleInputChange(data.questionId, next);
          }
        };
        return (
          <div className="w-full" {...rest}>
            <div className="flex flex-col md:flex-row gap-3 md:gap-4 md:items-end">
              {fields.map(f => (
                <div key={f.questionId} className="flex-1 min-w-[140px]">
                  {f.questionText && (
                    <label className="block text-sm mb-1 text-gray-700 dark:text-gray-300">
                      {f.questionText}
                    </label>
                  )}
                  {renderField(f, valueObj, setObj)}
                </div>
              ))}
            </div>
          </div>
        );
      }}
    />
  );
};

// Email Input Component
const EmailInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <input
        type="email"
        value={currentValue || ''}
        onChange={e => handleChange(e.target.value)}
        placeholder={
          data.placeholder || `Enter ${data.questionText.toLowerCase()}`
        }
        className={`${baseInputClasses} ${errorInputClasses}`}
        disabled={!isEditMode}
        required={data.isRequired}
      />
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <input
            type="email"
            value={value || currentValue || ''}
            onChange={e => {
              onChange(e.target.value);
              handleChange(e.target.value);
            }}
            placeholder={
              data.placeholder || `Enter ${data.questionText.toLowerCase()}`
            }
            className={`${baseInputClasses} ${errorInputClasses}`}
            disabled={!isEditMode}
            required={data.isRequired}
            {...rest}
          />
        );
      }}
    />
  );
};

// Phone Input Component
const PhoneInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatPhoneNumber,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <input
        type="tel"
        value={formatPhoneNumber(currentValue) || ''}
        onChange={e => handleChange(e.target.value)}
        placeholder={
          data.placeholder || `Enter ${data.questionText.toLowerCase()}`
        }
        className={`${baseInputClasses} ${errorInputClasses}`}
        disabled={!isEditMode}
        required={data.isRequired}
      />
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <input
            type="tel"
            value={
              formatPhoneNumber(value) || formatPhoneNumber(currentValue) || ''
            }
            onChange={e => {
              onChange(e.target.value);
              handleChange(e.target.value);
            }}
            placeholder={
              data.placeholder || `Enter ${data.questionText.toLowerCase()}`
            }
            className={`${baseInputClasses} ${errorInputClasses}`}
            disabled={!isEditMode}
            required={data.isRequired}
            {...rest}
          />
        );
      }}
    />
  );
};

// SSN Input Component
const SSNInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatSSN,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  // Get validation constraints
  const maxLength = data?.validation?.maxLength;
  const minLength = data?.validation?.minLength;

  if (!control) {
    return (
      <input
        type="text"
        value={formatSSN(currentValue) || ''}
        onChange={e => handleChange(e.target.value)}
        placeholder={
          data.placeholder || `Enter ${data.questionText.toLowerCase()}`
        }
        className={`${baseInputClasses} ${errorInputClasses}`}
        disabled={!isEditMode}
        required={data.isRequired}
        maxLength={maxLength}
        minLength={minLength}
      />
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <input
            type="text"
            value={formatSSN(value) || formatSSN(currentValue) || ''}
            onChange={e => {
              // Only allow digits and limit to maxLength if specified
              let inputValue = e.target.value.replace(/\D/g, '');
              // If maxLength is 9 (typical for SSN), enforce it
              if (maxLength && inputValue.length > maxLength) {
                inputValue = inputValue.substring(0, maxLength);
              }
              onChange(inputValue);
              handleChange(inputValue);
            }}
            placeholder={
              data.placeholder || `Enter ${data.questionText.toLowerCase()}`
            }
            className={`${baseInputClasses} ${errorInputClasses}`}
            disabled={!isEditMode}
            required={data.isRequired}
            maxLength={maxLength}
            minLength={minLength}
            {...rest}
          />
        );
      }}
    />
  );
};

// Date Input Component
const DateInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatDateForInput,
  formatCurrency,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';
  const formattedDateValue = formatDateForInput(currentValue);

  if (!control) {
    return (
      <input
        type="date"
        value={formattedDateValue || ''}
        onChange={e => handleChange(e.target.value)}
        className={`${baseInputClasses} ${errorInputClasses}`}
        disabled={!isEditMode}
        required={data.isRequired}
      />
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        const formattedValue =
          formatDateForInput(value) || formattedDateValue || '';
        return (
          <input
            type="date"
            value={formattedValue}
            onChange={e => {
              onChange(e.target.value);
              handleChange(e.target.value);
            }}
            className={`${baseInputClasses} ${errorInputClasses}`}
            disabled={!isEditMode}
            required={data.isRequired}
            {...rest}
          />
        );
      }}
    />
  );
};

// Textarea Input Component
const TextareaInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatCurrency,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <textarea
        value={currentValue || ''}
        onChange={e => handleChange(e.target.value)}
        placeholder={
          data.placeholder || `Enter ${data.questionText.toLowerCase()}`
        }
        className={`${baseInputClasses} ${errorInputClasses} min-h-[80px] resize-y`}
        disabled={!isEditMode}
        required={data.isRequired}
        rows={3}
      />
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <textarea
            value={value || currentValue || ''}
            onChange={e => {
              onChange(e.target.value);
              handleChange(e.target.value);
            }}
            placeholder={
              data.placeholder || `Enter ${data.questionText.toLowerCase()}`
            }
            className={`${baseInputClasses} ${errorInputClasses} min-h-[80px] resize-y`}
            disabled={!isEditMode}
            required={data.isRequired}
            rows={3}
            {...rest}
          />
        );
      }}
    />
  );
};

// Select Input Component
const SelectInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data?.answer ?? '';

  if (!control) {
    return (
      <select
        value={currentValue ?? ''}
        onChange={e => handleChange(e.target.value)}
        className={`${baseInputClasses} ${errorInputClasses}`}
        disabled={!isEditMode}
        required={data.isRequired}
      >
        <option value="">Select an option...</option>
        {data.options?.map((option, index) => {
          return (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          );
        })}
      </select>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue ?? ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        const normalizedValue =
          value != null && typeof value === 'object' && 'value' in value
            ? value.value
            : (value ?? currentValue ?? '');
        return (
          <select
            value={normalizedValue}
            onChange={e => {
              onChange(e.target.value);
              handleChange(e.target.value);
            }}
            className={`${baseInputClasses} ${errorInputClasses}`}
            disabled={!isEditMode}
            required={data.isRequired}
            {...rest}
          >
            <option value="">Select an option...</option>
            {data.options?.map((option, index) => {
              return (
                <option key={index} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </select>
        );
      }}
    />
  );
};

// Radio Input Component
const RadioInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <div className="space-y-2">
        {data.options?.map((option, index) => (
          <label
            key={index}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              type="radio"
              name={data.questionId}
              value={option.value}
              checked={currentValue === option.value}
              onChange={e => handleChange(e.target.value)}
              className="text-violet-600 focus:ring-violet-500 h-4 w-4"
              disabled={!isEditMode}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <div className="space-y-2">
            {data.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={data.questionId}
                  value={option.value}
                  checked={
                    value === option.value || currentValue === option.value
                  }
                  onChange={e => {
                    onChange(e.target.value);
                    handleChange(e.target.value);
                  }}
                  className="text-violet-600 focus:ring-violet-500 h-4 w-4"
                  disabled={!isEditMode}
                  {...rest}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );
      }}
    />
  );
};

// Checkbox Input Component
const CheckboxInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
}) => {
  const handleChange = (questionId, newValue) => {
    if (isEditMode && handleInputChange) {
      handleInputChange(questionId, newValue);
    }
  };

  const selectedValues = Array.isArray(data ? data.answer : [])
    ? data
      ? data.answer
      : data.answer || []
    : [];

  if (!control) {
    return (
      <div className="space-y-2">
        {data.options?.map((option, index) => (
          <label
            key={index}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              type="checkbox"
              value={option.value}
              checked={selectedValues.includes(option.value)}
              onChange={e => {
                let newValues = [...selectedValues];
                if (e.target.checked) {
                  if (option.exclusive) {
                    newValues = [option.value];
                  } else {
                    newValues.push(option.value);
                  }
                } else {
                  newValues = newValues.filter(v => v !== option.value);
                }
                handleChange(data.questionId, newValues);
              }}
              className="text-violet-600 focus:ring-violet-500 h-4 w-4 rounded"
              disabled={!isEditMode}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {option.label}
              {option.exclusive && (
                <span className="ml-1 text-xs text-purple-600 dark:text-purple-400">
                  (Exclusive)
                </span>
              )}
            </span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={selectedValues}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        const controllerValues = Array.isArray(value) ? value : selectedValues;
        return (
          <div className="space-y-2">
            {data.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={option.value}
                  checked={controllerValues.includes(option.value)}
                  onChange={e => {
                    let newValues = [...controllerValues];
                    if (e.target.checked) {
                      if (option.exclusive) {
                        newValues = [option.value];
                      } else {
                        newValues.push(option.value);
                      }
                    } else {
                      newValues = newValues.filter(v => v !== option.value);
                    }
                    onChange(newValues);
                    handleChange(data.questionId, newValues);
                  }}
                  className="text-violet-600 focus:ring-violet-500 h-4 w-4 rounded"
                  disabled={!isEditMode}
                  {...rest}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                  {option.exclusive && (
                    <span className="ml-1 text-xs text-purple-600 dark:text-purple-400">
                      (Exclusive)
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        );
      }}
    />
  );
};

// Boolean Input Component
const BooleanInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatCurrency,
}) => {
  const handleChange = (questionId, newValue) => {
    if (isEditMode && handleInputChange) {
      handleInputChange(questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <div className="space-y-2">
        {data.options?.map((option, index) => (
          <label
            key={index}
            className="flex items-center space-x-2 cursor-pointer"
          >
            <input
              type="radio"
              name={data.questionId}
              value={option.value}
              checked={
                currentValue === option.value ||
                (option.value === 'true' && currentValue === true) ||
                (option.value === 'false' && currentValue === false)
              }
              onChange={e =>
                handleChange(
                  data.questionId,
                  e.target.value === 'true' || e.target.value === true
                )
              }
              className="text-violet-600 focus:ring-violet-500 h-4 w-4"
              disabled={!isEditMode}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {option.label}
            </span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <div className="space-y-2">
            {data.options?.map((option, index) => (
              <label
                key={index}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={data.questionId}
                  value={option.value}
                  checked={
                    value === option.value ||
                    currentValue === option.value ||
                    (option.value === 'true' && value === true) ||
                    (option.value === 'false' && value === false) ||
                    (option.value === 'true' && currentValue === true) ||
                    (option.value === 'false' && currentValue === false)
                  }
                  onChange={e => {
                    const newValue =
                      e.target.value === 'true' || e.target.value === true;
                    onChange(newValue);
                    handleChange(data.questionId, newValue);
                  }}
                  className="text-violet-600 focus:ring-violet-500 h-4 w-4"
                  disabled={!isEditMode}
                  {...rest}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        );
      }}
    />
  );
};

// Repeater Input Component
const RepeaterInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatCurrency,
}) => {
  const handleChange = (questionId, newValue) => {
    if (isEditMode && handleInputChange) {
      handleInputChange(questionId, newValue);
    }
  };

  const items = Array.isArray(data ? data.answer : [])
    ? data
      ? data.answer
      : data.answer || []
    : [];

  if (!control) {
    return (
      <div className="space-y-3">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {items.length} item(s) added
        </div>
        {items.map((item, index) => (
          <div
            key={index}
            className="p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/50"
          >
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Item {index + 1}
            </div>
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {JSON.stringify(item, null, 2)}
            </div>
          </div>
        ))}
        {isEditMode && (
          <button
            type="button"
            className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
            onClick={() => {
              // TODO: Implement add item functionality
              console.log('Add item clicked for', data.questionId);
            }}
          >
            + Add Item
          </button>
        )}
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={items}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        const controllerItems = Array.isArray(value) ? value : items;
        return (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {controllerItems.length} item(s) added
            </div>
            {controllerItems.map((item, index) => (
              <div
                key={index}
                className="p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/50"
              >
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Item {index + 1}
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  {JSON.stringify(item, null, 2)}
                </div>
              </div>
            ))}
            {isEditMode && (
              <button
                type="button"
                className="text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300"
                onClick={() => {
                  // TODO: Implement add item functionality
                  console.log('Add item clicked for', data.questionId);
                }}
              >
                + Add Item
              </button>
            )}
          </div>
        );
      }}
    />
  );
};

// Dynamic Group Input Component (supports nested where.fields or legacy fields)
const DynamicGroupInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatDateForInput,
}) => {
  const fieldsDef = data?.where?.fields || data?.fields || [];
  const groupValidation =
    data?.where?.validation || data?.groupValidation || {};
  const minItems = groupValidation?.minItems;
  const maxItems = groupValidation?.maxItems;

  const initialItems = Array.isArray(data ? data.answer : [])
    ? data
      ? data.answer
      : data.answer || []
    : [];

  // Define formatSSN function within DynamicGroupInput component
  const formatSSN = ssnValue => {
    if (!ssnValue) return '';
    // If it's already formatted, return as is
    if (typeof ssnValue === 'string' && ssnValue.includes('-')) {
      return ssnValue;
    }
    // Format 9 digits as XXX-XX-XXXX
    const cleaned = ssnValue.toString().replace(/\D/g, '');
    if (cleaned.length === 9) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(
        5
      )}`;
    }
    return ssnValue;
  };

  // Render a sub-field input bound into items[idx][subField.questionId]
  const renderSubField = (fieldDef, itemValue, onItemsChange, idx) => {
    const t = (
      fieldDef?.questionType ||
      fieldDef?.inputType ||
      'text'
    ).toLowerCase();
    const qid = fieldDef.questionId;
    const value = itemValue?.[qid];

    const setItemProp = newVal => {
      onItemsChange(prev => {
        const next = Array.isArray(prev) ? [...prev] : [];
        const current = { ...(next[idx] || {}) };
        current[qid] = newVal;
        next[idx] = current;
        return next;
      });
    };

    const commonProps = {
      className: `${baseInputClasses} ${errorInputClasses}`,
      disabled: !isEditMode,
      required: fieldDef?.required,
      placeholder:
        fieldDef?.placeholder ||
        `Enter ${fieldDef?.questionText?.toLowerCase?.() || 'value'}`,
    };

    switch (t) {
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={e => setItemProp(e.target.value)}
            rows={3}
            {...commonProps}
          />
        );
      case 'number':
      case 'currency':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={e => setItemProp(e.target.value)}
            onWheel={e => e.target.blur()}
            className={`${commonProps.className} no-spinners`}
            min={fieldDef?.validation?.min}
            max={fieldDef?.validation?.max}
            disabled={commonProps.disabled}
            required={commonProps.required}
            placeholder={commonProps.placeholder}
          />
        );
      case 'date':
        return (
          <input
            type="date"
            value={formatDateForInput(value) || ''}
            onChange={e => setItemProp(e.target.value)}
            {...commonProps}
          />
        );
      case 'dropdown':
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={e => setItemProp(e.target.value)}
            className={`${baseInputClasses} ${errorInputClasses}`}
            disabled={!isEditMode}
            required={fieldDef?.required}
          >
            <option value="">Select an option...</option>
            {(fieldDef?.options || []).map((opt, i) => (
              <option key={i} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case 'radio':
        return (
          <div className="space-y-2">
            {(fieldDef?.options || []).map((opt, i) => (
              <label
                key={i}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={`${data.questionId}-${idx}-${qid}`}
                  value={opt.value}
                  checked={value === opt.value}
                  onChange={e => setItemProp(e.target.value)}
                  className="text-violet-600 focus:ring-violet-500 h-4 w-4"
                  disabled={!isEditMode}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        );
      case 'multi-select':
      case 'checkbox':
      case 'checkbox_multiple': {
        const arr = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {(fieldDef?.options || []).map((opt, i) => (
              <label
                key={i}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  value={opt.value}
                  checked={arr.includes(opt.value)}
                  onChange={e => {
                    let next = [...arr];
                    if (e.target.checked) {
                      if (opt.exclusive) next = [opt.value];
                      else next.push(opt.value);
                    } else {
                      next = next.filter(v => v !== opt.value);
                    }
                    setItemProp(next);
                  }}
                  className="text-violet-600 focus:ring-violet-500 h-4 w-4 rounded"
                  disabled={!isEditMode}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {opt.label}
                  {opt.exclusive && (
                    <span className="ml-1 text-xs text-purple-600 dark:text-purple-400">
                      (Exclusive)
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        );
      }
      case 'boolean':
      case 'toggle':
        return (
          <div className="space-y-2">
            {['true', 'false'].map(optVal => (
              <label
                key={optVal}
                className="flex items-center space-x-2 cursor-pointer"
              >
                <input
                  type="radio"
                  name={`${data.questionId}-${idx}-${qid}-bool`}
                  value={optVal}
                  checked={String(value) === optVal}
                  onChange={e => setItemProp(e.target.value === 'true')}
                  className="text-violet-600 focus:ring-violet-500 h-4 w-4"
                  disabled={!isEditMode}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {optVal === 'true' ? 'Yes' : 'No'}
                </span>
              </label>
            ))}
          </div>
        );
      case 'email':
        return (
          <input
            type="email"
            value={value || ''}
            onChange={e => setItemProp(e.target.value)}
            {...commonProps}
          />
        );
      case 'phone':
        return (
          <input
            type="tel"
            value={value || ''}
            onChange={e => setItemProp(e.target.value)}
            {...commonProps}
          />
        );
      case 'ssn':
        return (
          <input
            type="text"
            value={formatSSN(value) || ''}
            onChange={e => {
              // Only allow digits and limit to maxLength if specified
              let inputValue = e.target.value.replace(/\D/g, '');
              const maxLength = fieldDef?.validation?.maxLength;
              if (maxLength && inputValue.length > maxLength) {
                inputValue = inputValue.substring(0, maxLength);
              }
              setItemProp(inputValue);
            }}
            {...commonProps}
          />
        );
      case 'text':
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={e => setItemProp(e.target.value)}
            {...commonProps}
          />
        );
    }
  };

  if (!control) {
    // Read-only preview mode
    const items = initialItems;
    return (
      <div className="space-y-3">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {items.length} item(s) added
        </div>
        {items.map((it, idx) => (
          <div
            key={idx}
            className="p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/50"
          >
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Item {idx + 1}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {fieldsDef.map(fd => (
                <div
                  key={fd.questionId}
                  className="text-sm text-gray-700 dark:text-gray-300 break-words"
                >
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {fd.questionText || fd.questionId}
                  </div>
                  <div className="whitespace-pre-wrap">
                    {JSON.stringify(it?.[fd.questionId] ?? '', null, 2)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={initialItems}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        const items = Array.isArray(value) ? value : initialItems;

        const updateItems = updater => {
          const next = typeof updater === 'function' ? updater(items) : updater;
          onChange(next);
          if (isEditMode && handleInputChange) {
            handleInputChange(data.questionId, next);
          }
        };

        const canAdd = maxItems === undefined || items.length < maxItems;
        const canRemove = minItems === undefined || items.length > minItems;

        return (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {items.length} item(s) added
            </div>
            {items.map((it, idx) => (
              <div
                key={idx}
                className="p-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-gray-800/50"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    Item {idx + 1}
                  </div>
                  {isEditMode && (
                    <button
                      type="button"
                      className={`text-xs text-red-500 dark:text-red-400  hover:text-red-700 ${!canRemove ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() =>
                        canRemove &&
                        updateItems(items.filter((_, i) => i !== idx))
                      }
                      disabled={!canRemove}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {fieldsDef.map(fd => (
                    <div key={fd.questionId} className="flex flex-col gap-1">
                      <label className="text-xs text-gray-600 dark:text-gray-400">
                        {fd.questionText || fd.questionId}
                      </label>
                      {renderSubField(fd, it || {}, u => updateItems(u), idx)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {isEditMode && (
              <button
                type="button"
                className={`text-sm text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 ${!canAdd ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => canAdd && updateItems([...(items || []), {}])}
                disabled={!canAdd}
                {...rest}
              >
                + Add Item
              </button>
            )}
          </div>
        );
      }}
    />
  );
};

// File Input Component
const FileInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatCurrency,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  if (!control) {
    return (
      <FileUploader
        type="file"
        onChange={e => handleChange(e.target.files)}
        className={baseInputClasses}
        disabled={!isEditMode}
        accept={data.acceptedFormats?.join(',')}
      />
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      render={({ field }) => {
        // Omit `value` to avoid binding a value to <input type="file"> which throws InvalidStateError
        const { onChange, value, ...rest } = field;
        return (
          <FileUploader
            data={data}
            onFileUpload={files => {
              onChange(files);
              handleChange(files);
            }}
            className={baseInputClasses}
            disabled={!isEditMode}
            accept={data.acceptedFormats?.join(',')}
            {...rest}
          />
        );
      }}
    />
  );
};

// Currency Input Component
const CurrencyInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatCurrency,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <div className="relative">
        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
          $
        </span>
        <input
          type="number"
          value={currentValue || ''}
          onChange={e => handleChange(e.target.value)}
          onWheel={e => e.target.blur()}
          placeholder={
            data.placeholder || `Enter ${data.questionText.toLowerCase()}`
          }
          className={`no-spinners pl-8 ${baseInputClasses} ${errorInputClasses}`}
          disabled={!isEditMode}
          required={data.isRequired}
          min={data.validation?.min}
          max={data.validation?.max}
          step="0.01"
        />
      </div>
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
              $
            </span>
            <input
              type="number"
              value={value || currentValue || ''}
              onChange={e => {
                onChange(e.target.value);
                handleChange(e.target.value);
              }}
              onWheel={e => e.target.blur()}
              placeholder={
                data.placeholder || `Enter ${data.questionText.toLowerCase()}`
              }
              className={`no-spinners pl-8 ${baseInputClasses} ${errorInputClasses}`}
              disabled={!isEditMode}
              required={data.isRequired}
              min={data.validation?.min}
              max={data.validation?.max}
              step="0.01"
              {...rest}
            />
          </div>
        );
      }}
    />
  );
};

// Number Input Component
const NumberInput = ({
  data,
  control,
  errors,
  handleInputChange,
  isEditMode,
  baseInputClasses,
  errorInputClasses,
  formatCurrency,
}) => {
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const currentValue = data ? data.answer : '';

  if (!control) {
    return (
      <input
        type="number"
        value={currentValue || ''}
        onChange={e => handleChange(e.target.value)}
        onWheel={e => e.target.blur()}
        placeholder={
          data.placeholder || `Enter ${data.questionText.toLowerCase()}`
        }
        className={`no-spinners ${baseInputClasses} ${errorInputClasses}`}
        disabled={!isEditMode}
        required={data.isRequired}
        min={data.validation?.min}
        max={data.validation?.max}
      />
    );
  }

  return (
    <Controller
      name={data.questionId}
      control={control}
      defaultValue={currentValue || ''}
      render={({ field }) => {
        const { value, onChange, ...rest } = field;
        return (
          <input
            type="number"
            value={value || currentValue || ''}
            onChange={e => {
              onChange(e.target.value);
              handleChange(e.target.value);
            }}
            onWheel={e => e.target.blur()}
            placeholder={
              data.placeholder || `Enter ${data.questionText.toLowerCase()}`
            }
            className={`no-spinners ${baseInputClasses} ${errorInputClasses}`}
            disabled={!isEditMode}
            required={data.isRequired}
            min={data.validation?.min}
            max={data.validation?.max}
            {...rest}
          />
        );
      }}
    />
  );
};

const QuestionInput = props => {
  // props
  const { data, control, errors, handleInputChange, isEditMode } = props;

  // vars
  const baseInputClasses = `w-full md:w-1/2 px-3 py-2 border rounded-md transition-colors text-sm ${
    isEditMode
      ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500'
      : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300 cursor-not-allowed'
  }`;
  const errorInputClasses =
    errors && errors[data.questionId]
      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
      : '';

  // handlers
  const handleChange = newValue => {
    if (isEditMode && handleInputChange) {
      handleInputChange(data.questionId, newValue);
    }
  };

  const formatDateForInput = dateValue => {
    if (!dateValue) return '';

    const pad2 = n => String(n).padStart(2, '0');
    const toYMDLocal = d => {
      if (!(d instanceof Date) || isNaN(d)) return '';
      // Use local components to avoid timezone-based off-by-one issues
      const y = d.getFullYear();
      const m = pad2(d.getMonth() + 1);
      const day = pad2(d.getDate());
      return `${y}-${m}-${day}`;
    };

    // Strings
    if (typeof dateValue === 'string') {
      const str = dateValue.trim();

      // Already YYYY-MM-DD
      const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymd) {
        // Validate
        const d = new Date(`${str}T00:00:00`);
        if (!isNaN(d)) return str;
      }

      // ISO-like with time (e.g. YYYY-MM-DDTHH:mm:ssZ or space)
      const isoPrefix = str.match(/^(\d{4}-\d{2}-\d{2})[T\s]/);
      if (isoPrefix) return isoPrefix[1];

      // YYYY/MM/DD or YYYY.MM.DD
      let m = str.match(/^(\d{4})[\/.\-](\d{1,2})[\/.\-](\d{1,2})$/);
      if (m) {
        const [, y, mo, da] = m;
        const d = new Date(Number(y), Number(mo) - 1, Number(da));
        if (!isNaN(d)) return `${y}-${pad2(mo)}-${pad2(da)}`;
      }

      // MM/DD/YYYY or M/D/YYYY or MM-DD-YYYY (heuristic for D/M/Y if first > 12)
      m = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
      if (m) {
        let [, a, b, y] = m;
        let month = Number(a);
        let day = Number(b);
        if (month > 12) {
          // Interpret as D/M/Y
          day = Number(a);
          month = Number(b);
        }
        const d = new Date(Number(y), month - 1, day);
        if (!isNaN(d)) return `${y}-${pad2(month)}-${pad2(day)}`;
      }

      // DD.MM.YYYY or D.M.YYYY
      m = str.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (m) {
        const [, dd, mm, y] = m;
        const d = new Date(Number(y), Number(mm) - 1, Number(dd));
        if (!isNaN(d)) return `${y}-${pad2(mm)}-${pad2(dd)}`;
      }

      // Month DD, YYYY (e.g., Jan 2, 2023 or January 2, 2023)
      m = str.match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})$/);
      if (m) {
        const [, monName, dd, y] = m;
        const months = [
          'january',
          'february',
          'march',
          'april',
          'may',
          'june',
          'july',
          'august',
          'september',
          'october',
          'november',
          'december',
        ];
        const idx = months.findIndex(mn =>
          mn.startsWith(monName.toLowerCase())
        );
        if (idx >= 0) return `${y}-${pad2(idx + 1)}-${pad2(dd)}`;
      }

      // Fallback: let Date try to parse, then normalize using local components
      const parsed = new Date(str);
      if (!isNaN(parsed)) return toYMDLocal(parsed);
      return '';
    }

    // Date object
    if (dateValue instanceof Date) {
      return toYMDLocal(dateValue);
    }

    // Timestamp (ms)
    if (typeof dateValue === 'number') {
      const d = new Date(dateValue);
      return toYMDLocal(d);
    }

    return '';
  };

  const formatSSN = ssnValue => {
    if (!ssnValue) return '';
    // If it's already formatted, return as is
    if (typeof ssnValue === 'string' && ssnValue.includes('-')) {
      return ssnValue;
    }
    // Format 9 digits as XXX-XX-XXXX
    const cleaned = ssnValue.toString().replace(/\D/g, '');
    if (cleaned.length === 9) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(
        5
      )}`;
    }
    return ssnValue;
  };

  const formatPhoneNumber = phoneValue => {
    if (!phoneValue) return '';
    // If it's already formatted, return as is
    if (
      typeof phoneValue === 'string' &&
      phoneValue.replace(/\D/g, '').length === 10
    ) {
      const cleaned = phoneValue.replace(/\D/g, '');
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6
      )}`;
    }
    return phoneValue;
  };

  const formatCurrency = currencyValue => {
    if (!currencyValue) return '';
    // If it's already formatted, return as is
    if (typeof currencyValue === 'string' && currencyValue.includes('$')) {
      return currencyValue;
    }
    // Format as currency
    const cleaned = currencyValue.toString().replace(/\D/g, '');
    if (cleaned.length > 0) {
      return `$${cleaned}`;
    }
    return currencyValue;
  };

  const normalizeType = type => {
    const t = (type || '').toLowerCase();
    if (t === 'dropdown') return 'select';
    if (t === 'checkbox_multiple') return 'multi-select';
    if (t === 'dob' || t === 'date_of_birth') return 'date';
    return t;
  };
  const effectiveType = normalizeType(data?.inputType || data?.questionType);

  switch (effectiveType) {
    case 'text':
      return (
        <TextInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatPhoneNumber={formatPhoneNumber}
          formatSSN={formatSSN}
          formatDateForInput={formatDateForInput}
          formatCurrency={formatCurrency}
        />
      );
    case 'email':
      return (
        <EmailInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
        />
      );
    case 'phone':
      return (
        <PhoneInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatPhoneNumber={formatPhoneNumber}
        />
      );
    case 'ssn':
      return (
        <SSNInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatSSN={formatSSN}
        />
      );
    case 'date':
      return (
        <DateInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatDateForInput={formatDateForInput}
          formatCurrency={formatCurrency}
        />
      );
    case 'textarea':
      return (
        <TextareaInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'select':
      return (
        <SelectInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'group':
      return (
        <GroupInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={`${baseInputClasses} w-full md:w-full`}
          errorInputClasses={`${errorInputClasses} w-full md:w-full`}
          formatDateForInput={formatDateForInput}
        />
      );
    case 'radio':
      return (
        <RadioInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'checkbox':
      return (
        <CheckboxInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'multi-select':
      return (
        <CheckboxInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'boolean':
      return (
        <BooleanInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'repeater':
      return (
        <RepeaterInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'dynamic_group':
      return (
        <DynamicGroupInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatDateForInput={formatDateForInput}
        />
      );
    case 'file_upload':
      return (
        <FileInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={`${baseInputClasses} w-full md:w-full`}
          errorInputClasses={`${errorInputClasses} w-full md:w-full`}
          formatCurrency={formatCurrency}
        />
      );
    case 'currency':
      return (
        <CurrencyInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    case 'number':
      return (
        <NumberInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatCurrency={formatCurrency}
        />
      );
    default:
      // Default to text input for unknown types
      return (
        <TextInput
          data={data}
          control={control}
          errors={errors}
          handleInputChange={handleInputChange}
          isEditMode={isEditMode}
          baseInputClasses={baseInputClasses}
          errorInputClasses={errorInputClasses}
          formatPhoneNumber={formatPhoneNumber}
          formatSSN={formatSSN}
          formatDateForInput={formatDateForInput}
          formatCurrency={formatCurrency}
        />
      );
  }
};

export default QuestionInput;
